import PropTypes from 'prop-types';
import styles from './FJAiVideoDubRightSuccess.module.less';
import { useRef, useEffect, useState } from 'react';
import FJVideoBox from '../../../../common/components/FJVideoPlayer/FJVideoBox/FJVideoBox';
import { useCallback } from 'react';
import FJConstant from '../../../../constants/FJConstant';
import FJLocalStore from '../../../../store/FJLocalStore';
import FJResourceItemLoading from '../../../../common/components/FJResourceItemLoading/FJResourceItemLoading';
import FJUtil from '../../../../util/FJUtil';

const FJAiVideoDubRightSuccess = ({
    isShow,
    resultInfo,
    audioWavesurferUrl,
    updateAudioWavesurferUrl,
    resultAudioInfoStatus,
    retryAudio,
    isShowTrimVideo,
}) => {
    const { videoUrl, width, height, duration } = resultInfo;
    const [stateVideoUrl, setStateVideoUrl] = useState(videoUrl);

    const audioWavesurferUrlRef = useRef(null);
    const videoBoxRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [thumbnailRect, setThumbnailRect] = useState({ width: 0, height: 0 });
    const isPlayingRef = useRef(false);
    const mouseDownPlaying = useRef(false);

    const requestAnimationFrame = useRef(null);

    useEffect(() => {
        setStateVideoUrl(videoUrl);
    }, [videoUrl]);

    const updateAudioThumbnail = useCallback(() => {
        if (audioWavesurferUrlRef.current) {
            const viewSize = {
                width: audioWavesurferUrlRef.current.offsetWidth,
                height: audioWavesurferUrlRef.current.offsetHeight,
            };
            updateAudioWavesurferUrl(viewSize);
        }
    }, [updateAudioWavesurferUrl]);

    const updateThumbnailRect = useCallback(() => {
        if (!audioWavesurferUrlRef.current) return;
        const thumbnailBox = audioWavesurferUrlRef.current.getBoundingClientRect();

        setThumbnailRect({
            width: thumbnailBox.width,
            height: thumbnailBox.height,
        });
    }, []);

    useEffect(() => {
        updateThumbnailRect();
        updateAudioThumbnail();

        window.addEventListener('resize', updateThumbnailRect);
        window.addEventListener('resize', updateAudioThumbnail);
        return () => {
            window.removeEventListener('resize', updateThumbnailRect);
            window.removeEventListener('resize', updateAudioThumbnail);
        };
    }, [updateThumbnailRect, updateAudioThumbnail]);

    const play = useCallback(() => {
        if (videoBoxRef.current) {
            videoBoxRef.current.play();
            setIsPlaying(true);
            isPlayingRef.current = true;
        }
    }, []);

    const pause = useCallback(() => {
        if (videoBoxRef.current) {
            videoBoxRef.current.pause();
            setIsPlaying(false);
            isPlayingRef.current = false;
            if (requestAnimationFrame.current) {
                window.cancelAnimationFrame(requestAnimationFrame.current);
                requestAnimationFrame.current = null;
            }
        }
    }, []);

    const seekVideo = useCallback(
        (time, isPause = false) => {
            if (videoBoxRef.current) {
                videoBoxRef.current.seek(time);
                setCurrentTime(time);
                if (isPause) {
                    pause();
                }
            }
        },
        [pause],
    );

    const getElementRect = useCallback(element => {
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        return {
            left: rect.left + window.scrollX,
            width: rect.width,
            right: rect.right + window.scrollX,
        };
    }, []);

    const calculateTimeFromEvent = useCallback(
        event => {
            if (!audioWavesurferUrlRef.current || duration <= 0) return 0;

            const rect = getElementRect(audioWavesurferUrlRef.current);
            if (!rect) return 0;

            const x = Math.max(rect.left, Math.min(event.clientX, rect.right));
            const offsetX = x - rect.left;

            const time = (offsetX / rect.width) * duration;
            return Math.max(0, Math.min(time, duration));
        },
        [duration, getElementRect],
    );

    const handleMouseMove = useCallback(
        e => {
            const time = calculateTimeFromEvent(e);
            seekVideo(time, true);
        },
        [seekVideo, calculateTimeFromEvent],
    );

    const handleMouseUp = useCallback(() => {
        if (mouseDownPlaying.current) {
            play();
        }
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove, play]);

    const thumbnailMouseDown = useCallback(
        e => {
            if (resultAudioInfoStatus !== FJConstant.SUCCESS) {
                return;
            }

            mouseDownPlaying.current = isPlayingRef.current;
            const time = calculateTimeFromEvent(e);
            seekVideo(time, true);

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);

            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        },
        [seekVideo, calculateTimeFromEvent, handleMouseMove, handleMouseUp, resultAudioInfoStatus],
    );

    const handleKeyDown = useCallback(
        e => {
            if (e.key === ' ' || e.key === 'Spacebar') {
                if (isPlaying) {
                    pause();
                } else {
                    play();
                }
            }
        },
        [isPlaying, play, pause],
    );

    useEffect(() => {
        isShow && !isShowTrimVideo && window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, isShow, isShowTrimVideo]);

    const videoPlaying = useCallback(() => {
        if (videoBoxRef.current) {
            let requestAnimationTime;
            let playTime = currentTime;
            let __playing = timestamp => {
                if (!requestAnimationTime) requestAnimationTime = timestamp;

                playTime += (timestamp - requestAnimationTime) / 1000;
                requestAnimationTime = timestamp;

                if (playTime >= 0 && playTime <= duration) {
                    setCurrentTime(playTime);
                }

                if (playTime >= duration) {
                    seekVideo(0);
                    isPlaying && pause();
                } else {
                    requestAnimationFrame.current = window.requestAnimationFrame(__playing);
                }
            };

            if (requestAnimationFrame.current) window.cancelAnimationFrame(requestAnimationFrame.current);

            setIsPlaying(true);
            isPlayingRef.current = true;
            requestAnimationFrame.current = window.requestAnimationFrame(__playing);
        }
    }, [currentTime, duration, isPlaying, pause, seekVideo]);

    const videoOnClick = e => {
        e.stopPropagation();
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    };

    const onReload = () => {
        setStateVideoUrl(FJUtil.getReloadNewUrl(stateVideoUrl));
    };

    useEffect(() => {
        if (!isShow) {
            pause();
        }
    }, [isShow, pause]);

    useEffect(() => {
        if (isShow) {
            setTimeout(() => {
                updateThumbnailRect();
                updateAudioThumbnail();
                videoBoxRef.current.fitVideoSize();
            }, 200);
        }
    }, [isShow, updateThumbnailRect, updateAudioThumbnail]);

    return (
        <div className={styles.box} style={{ display: isShow ? 'flex' : 'none' }}>
            <div className={styles.video_box} onClick={videoOnClick}>
                <FJVideoBox
                    src={stateVideoUrl}
                    onPlaying={videoPlaying}
                    autoPlay={isShow}
                    ref={videoBoxRef}
                    ratio={width / height}
                    onReload={onReload}
                />
            </div>
            <div className={styles.audio_thumbnail_box} onMouseDown={thumbnailMouseDown} ref={audioWavesurferUrlRef}>
                {resultAudioInfoStatus === FJConstant.SUCCESS && (
                    <>
                        <img src={audioWavesurferUrl} alt='audio' />

                        <div
                            className={styles.progress_line_box}
                            style={{ left: `${Math.round((currentTime / duration) * thumbnailRect.width)}px` }}
                        >
                            <div className={styles.progress_line}></div>
                        </div>

                        {!isPlaying && (
                            <div
                                className={styles.progress_time}
                                style={{ left: `${(currentTime / duration) * 100}%` }}
                            >
                                {new Date(currentTime * 1000).toISOString().substr(14, 5)}/
                                {new Date(duration * 1000).toISOString().substr(14, 5)}
                            </div>
                        )}
                    </>
                )}

                {resultAudioInfoStatus === FJConstant.ERROR && (
                    <div className={styles.error_box}>
                        <div className={styles.error_btu} onClick={retryAudio}>
                            {FJLocalStore._('retry')}
                        </div>
                    </div>
                )}

                {resultAudioInfoStatus === FJConstant.LOADING && <FJResourceItemLoading />}
            </div>
        </div>
    );
};

FJAiVideoDubRightSuccess.propTypes = {
    resultInfo: PropTypes.object.isRequired,
    audioWavesurferUrl: PropTypes.string.isRequired,
    updateAudioWavesurferUrl: PropTypes.func.isRequired,
    resultAudioInfoStatus: PropTypes.string.isRequired,
    retryAudio: PropTypes.func.isRequired,
    isShow: PropTypes.bool.isRequired,
    isShowTrimVideo: PropTypes.bool.isRequired,
};

export default FJAiVideoDubRightSuccess;
